import { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileSpreadsheet, ArrowRight, CheckCircle2, AlertTriangle, Contact } from "lucide-react";

const BADGE = {
    fill: ["Fill blanks", "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"],
    conflict: ["Different", "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"],
    clean: ["Up to date", "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"],
    no_match: ["No match", "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"],
    invalid: ["Invalid", "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"],
};

/** CSV → add missing email / phone to existing customers. Never creates customers. */
export function ContactFillDialog({ open, setOpen, onDone }) {
    const [step, setStep] = useState("upload");
    const [preview, setPreview] = useState(null);
    const [decisions, setDecisions] = useState({});
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);

    const reset = () => { setStep("upload"); setPreview(null); setDecisions({}); setResult(null); };

    const downloadTemplate = async () => {
        const { data } = await api.get("/customers/contacts/template.csv", { responseType: "blob" });
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = "customer_contacts_template.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    const analyze = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) return toast.error("Pick a CSV first");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/customers/contacts/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            setDecisions(Object.fromEntries(data.items.map((it) => [it.row.csv_row, it.suggested_action])));
            setStep("review");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    };

    const commit = async () => {
        setBusy(true);
        try {
            const payload = {
                decisions: preview.items.filter((it) => it.matched_customer_id).map((it) => ({
                    matched_customer_id: it.matched_customer_id,
                    action: decisions[it.row.csv_row] || "skip",
                    email: it.row.email, phone: it.row.phone,
                })),
            };
            const { data } = await api.post("/customers/contacts/commit", payload);
            setResult(data);
            setStep("done");
            toast.success(`Updated ${data.updated} customer${data.updated === 1 ? "" : "s"}`);
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Commit failed");
        } finally { setBusy(false); }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="contact-fill-dialog">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2"><Contact className="w-4 h-4" />Fill missing contacts</SheetTitle>
                    <SheetDescription>
                        Upload a CSV of emails and phone numbers. Rows are matched by account # → name + address → name.
                        Blank fields get filled; different values are skipped unless you pick Overwrite.
                    </SheetDescription>
                </SheetHeader>
                {step === "upload" && (
                    <UploadStep fileRef={fileRef} busy={busy} onTemplate={downloadTemplate}
                                onAnalyze={analyze} onCancel={() => setOpen(false)} />
                )}
                {step === "review" && preview && (
                    <ReviewStep preview={preview} decisions={decisions} setDecisions={setDecisions}
                                busy={busy} onBack={reset} onCommit={commit} />
                )}
                {step === "done" && result && (
                    <div className="mt-6 space-y-4" data-testid="contact-fill-done">
                        <div className="flex items-center gap-3 p-4 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900">
                            <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
                            <div className="text-sm" data-testid="contact-fill-result">
                                <b>{result.updated}</b> updated · {result.skipped} skipped
                                {result.errors > 0 && ` · ${result.errors} errors`}
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={() => { reset(); setOpen(false); }} data-testid="contact-fill-close">Done</Button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function UploadStep({ fileRef, busy, onTemplate, onAnalyze, onCancel }) {
    return (
        <div className="mt-6 space-y-4">
            <button onClick={onTemplate} className="text-xs text-primary hover:underline flex items-center gap-1"
                    data-testid="contact-fill-template">
                <Download className="w-3 h-3" />Download CSV template
            </button>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <div className="text-xs text-muted-foreground mb-3">
                    Columns: account_number and/or name (+ address_line1, zip), email, phone
                </div>
                <input ref={fileRef} type="file" accept=".csv"
                       className="block w-full text-sm border border-input rounded p-2 bg-background"
                       data-testid="contact-fill-file" />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button onClick={onAnalyze} disabled={busy} data-testid="contact-fill-analyze">
                    {busy ? "Analyzing..." : <>Analyze CSV<ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
            </div>
        </div>
    );
}

function ReviewStep({ preview, decisions, setDecisions, busy, onBack, onCommit }) {
    const c = preview.counts;
    const willChange = preview.items.filter((it) => ["fill", "overwrite"].includes(decisions[it.row.csv_row])).length;
    return (
        <div className="mt-6 space-y-4">
            {preview.warnings?.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 rounded text-xs text-amber-800 dark:text-amber-300"
                     data-testid="contact-fill-warnings">
                    <AlertTriangle className="w-4 h-4 shrink-0" />{preview.warnings.join(" · ")}
                </div>
            )}
            <div className="grid grid-cols-5 gap-2 text-center" data-testid="contact-fill-counts">
                {["fill", "conflict", "clean", "no_match", "invalid"].map((k) => (
                    <div key={k} className="border border-border rounded p-2 bg-secondary/30">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{BADGE[k][0]}</div>
                        <div className="text-xl font-bold tabular" data-testid={`contact-count-${k}`}>{c[k] ?? 0}</div>
                    </div>
                ))}
            </div>
            <div className="border border-border rounded-md max-h-[55vh] overflow-y-auto" data-testid="contact-fill-table">
                {preview.items.map((it) => (
                    <ContactRow key={it.row.csv_row} item={it} action={decisions[it.row.csv_row] || "skip"}
                                onAction={(a) => setDecisions((d) => ({ ...d, [it.row.csv_row]: a }))} />
                ))}
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground">On commit: <b>{willChange}</b> customer(s) updated</div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={onBack}>Back</Button>
                    <Button onClick={onCommit} disabled={busy || willChange === 0} data-testid="contact-fill-commit">
                        {busy ? "Saving..." : `Apply ${willChange} update${willChange === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ContactRow({ item, action, onAction }) {
    const r = item.row;
    const [label, cls] = BADGE[item.category];
    const matched = !!item.matched_customer_id;
    const hasFill = matched && Object.values(item.fields).some((f) => f.state === "fill");
    const hasConflict = item.category === "conflict";
    return (
        <div className="border-b border-border last:border-0 p-3 flex items-start gap-3" data-testid={`contact-row-${r.csv_row}`}>
            <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded shrink-0 mt-0.5 ${cls}`}>{label}</span>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">
                    {item.existing_name || r.name || "—"}
                    <span className="text-xs text-muted-foreground font-normal ml-2">
                        {item.existing_account_number || r.account_number || ""}
                        {matched && ` · matched by ${item.matched_on.replace("_", " + ")}`}
                    </span>
                </div>
                {matched ? Object.entries(item.fields).filter(([, f]) => f.state !== "none").map(([k, f]) => (
                    <div key={k} className="text-[11px] flex gap-2 items-baseline mt-0.5">
                        <span className="font-mono text-muted-foreground w-12 shrink-0">{k}</span>
                        <span className={f.state === "conflict" ? "line-through text-muted-foreground truncate" : "text-muted-foreground truncate"}>
                            {f.existing || "(blank)"}
                        </span>
                        {f.state !== "same" && <ArrowRight className="w-3 h-3 shrink-0" />}
                        {f.state !== "same" && <span className="font-medium truncate">{f.incoming}</span>}
                    </div>
                )) : (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        {item.reason || "No existing customer matches this row — skipped"}
                    </div>
                )}
            </div>
            {matched && (hasFill || hasConflict) && (
                <Select value={action} onValueChange={onAction}>
                    <SelectTrigger className="w-32 h-8 text-xs shrink-0" data-testid={`contact-action-${r.csv_row}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {hasFill && <SelectItem value="fill">Fill blanks</SelectItem>}
                        {hasConflict && <SelectItem value="overwrite">Overwrite</SelectItem>}
                        <SelectItem value="skip">Skip</SelectItem>
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}
